import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { API_BASE_URL, PUBLIC_URL } from '../config/api';
import { clearWebsiteToken, getoptionalAppAuthHeaders, isWebsiteLoggedIn } from '../utils/optionalAppAuth';
import { getGuestCartCount } from '../utils/guestCart';
import { fetchWebsiteCategories } from '../utils/websiteCategories';
import profilePlaceholder from './../assets/images/student_placeholder 1.webp';
import { withFallbackImage } from "../utils/imageFallback"
import { FaGift } from "react-icons/fa";
import logo from './../assets/images/background/artTokri_logo.webp';
import Collapse from 'react-bootstrap/Collapse';
import { MenuListArray2 } from './MenuListArray2';
import { FaSearch } from "react-icons/fa";
import { deleteToken, getMessaging } from 'firebase/messaging';
import GoogleTranslate from "../components/GoogleTranslate";
import './HeaderFestival.css';

// Initials Avatar Component
const getInitials = (name) => {
    if (!name) return 'G';
    const names = name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

const getInitialsColor = (name) => {
    const colors = [
        '#FF6B35', '#4CAF50', '#2196F3', '#9C27B0', '#FF9800',
        '#E91E63', '#00BCD4', '#3F51B5', '#FFC107', '#795548',
        '#607D8B', '#8BC34A', '#FF5722', '#673AB7', '#009688'
    ];
    let hash = 0;
    if (name) {
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
};

const InitialsAvatar = ({ name, size = 40 }) => {
    const initials = getInitials(name);
    const backgroundColor = getInitialsColor(name);

    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: '50%',
                backgroundColor: backgroundColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: size * 0.4,
                fontWeight: 600,
                color: '#ffffff',
                textTransform: 'uppercase'
            }}
        >
            {initials}
        </div>
    );
};

function Header() {
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const navigate = useNavigate();
    const [selectBtn, setSelectBtn] = useState('All Categories');
    const [searchText, setSearchText] = useState('');
    const [searchCategories, setSearchCategories] = useState([]);
    const [cartCount, setCartCount] = useState(0);
    const [wishlistCount, setWishlistCount] = useState(0);
    const [isProfileCompleted, setIsProfileCompleted] = useState(false);
    const [profileData, setProfileData] = useState({
        full_name: 'Guest',
        email: '',
        profileImage: ''
    });
    const [imageError, setImageError] = useState(false);
    const [headerFix, setheaderFix] = React.useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Mobile detection
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 992);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", () => {
            setheaderFix(window.scrollY > 50);
        });
    }, []);

    const showSidebar = () => setSidebarOpen(!sidebarOpen);

    useEffect(() => {
        const handleClickOutside = (e) => {
            const sidebar = document.getElementById("navbarNavDropdown");
            const toggleBtn = document.querySelector(".navbar-toggler");

            if (
                sidebarOpen &&
                sidebar &&
                !sidebar.contains(e.target) &&
                !toggleBtn.contains(e.target)
            ) {
                setSidebarOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [sidebarOpen]);

    useEffect(() => {
        var mainMenu = document.getElementById('OpenMenu');
        if (mainMenu) {
            if (sidebarOpen) {
                mainMenu.classList.add('show');
            } else {
                mainMenu.classList.remove('show');
            }
        }
    });

    const location = useLocation();
    const isActiveRoute = (path) => {
        if (!path) return false;
        if (path === "/") {
            return location.pathname === "/";
        }
        return location.pathname.startsWith(path);
    };

    const getFullImageUrl = (imagePath) => {
        if (!imagePath) return '';
        if (imagePath.startsWith('http') || imagePath.startsWith('blob:')) {
            return imagePath;
        }
        return `${PUBLIC_URL}/${imagePath.replace(/\\/g, '/').replace(/^\/+/, '')}`;
    };

    const fetchBadgeCounts = useCallback(async () => {
        if (!isWebsiteLoggedIn()) {
            setCartCount(getGuestCartCount());
            setWishlistCount(0);
            setProfileData({ full_name: 'Guest', email: '', profileImage: '' });
            return;
        }

        try {
            const meRes = await fetch(`${API_BASE_URL}/server/website/auth/me`, {
                headers: { ...getoptionalAppAuthHeaders() }
            });

            const meJson = await meRes.json();
            const isProfileCompleted = meJson?.data?.user?.is_profile_completed;
            setIsProfileCompleted(isProfileCompleted);
            if (!isProfileCompleted) {
                setCartCount(getGuestCartCount());
                setWishlistCount(0);
                setProfileData({ full_name: 'Guest', email: '', profileImage: '' });
                return;
            }

            const [cartRes, wishlistRes] = await Promise.all([
                fetch(`${API_BASE_URL}/server/website/cart`, { headers: { ...getoptionalAppAuthHeaders() } }),
                fetch(`${API_BASE_URL}/server/website/wishlist`, { headers: { ...getoptionalAppAuthHeaders() } })
            ]);

            const cartJson = await cartRes.json();
            const wishlistJson = await wishlistRes.json();

            const cartItems = cartJson?.data?.cart || [];
            const totalCartQty = cartItems.reduce((sum, item) => sum + Number(item?.quantity || 0), 0);

            setCartCount(totalCartQty);
            setWishlistCount((wishlistJson?.data?.wishlist || []).length);

            setProfileData({
                full_name: meJson?.data?.user?.full_name || 'User',
                email: meJson?.data?.user?.email || '',
                profileImage: meJson?.data?.user?.profile_image || ''
            });

        } catch (error) {
            setCartCount(0);
            setWishlistCount(0);
            setProfileData({ full_name: 'Guest', email: '', profileImage: '' });
        }
    }, []);

    useEffect(() => {
        const handleProfileUpdate = () => {
            fetchBadgeCounts();
        };
        window.addEventListener("profileUpdated", handleProfileUpdate);
        return () => window.removeEventListener("profileUpdated", handleProfileUpdate);
    }, [fetchBadgeCounts]);

    useEffect(() => {
        if (profileData.profileImage) {
            setImageError(false);
        }
    }, [profileData.profileImage]);

    useEffect(() => {
        const handleInstantProfileImage = (event) => {
            if (event.detail?.profile_image) {
                setImageError(false);
                setProfileData((prev) => ({
                    ...prev,
                    profileImage: event.detail.profile_image
                }));
            }
        };
        window.addEventListener("profileImageUpdated", handleInstantProfileImage);
        return () => window.removeEventListener("profileImageUpdated", handleInstantProfileImage);
    }, []);

    useEffect(() => {
        fetchBadgeCounts();
    }, [fetchBadgeCounts, location.pathname]);

    useEffect(() => {
        const onUpdate = () => fetchBadgeCounts();
        window.addEventListener("cartWishlistUpdated", onUpdate);
        return () => window.removeEventListener("cartWishlistUpdated", onUpdate);
    }, [fetchBadgeCounts]);

    useEffect(() => {
        const fetchSearchCategories = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/server/website/product/categories`, { headers: { ...getoptionalAppAuthHeaders() } });
                const result = await response.json();
                if (response.ok && result?.success) {
                    setSearchCategories(result.data || []);
                }
            } catch (error) { }
        };
        fetchSearchCategories();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const category = (params.get('category') || '').trim();
        const search = (params.get('search') || '').trim();
        setSearchText(search);
        setSelectBtn(category || 'All Categories');
    }, [location.search]);

    const selectedCategory = useMemo(() => (
        selectBtn === 'All Categories' ? '' : selectBtn
    ), [selectBtn]);

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        const params = new URLSearchParams();
        const textValue = searchText.trim();
        if (textValue) params.set('search', textValue);
        if (selectedCategory) params.set('category', selectedCategory);
        const query = params.toString();
        navigate(`/shop${query ? `?${query}` : ''}`);
    };

    const hasValidProfileImage = () => {
        return !imageError && profileData.profileImage && profileData.profileImage !== '';
    };

    const handleLogout = async () => {
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        setShowProfileDropdown(false);

        try {
            const response = await fetch(`${API_BASE_URL}/server/website/user/logout`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...getoptionalAppAuthHeaders()
                }
            });

            const result = await response.json();

            if (response.ok && result?.success) {
                setSidebarOpen(false);
                clearWebsiteToken();
                localStorage.removeItem("full_name");
                setCartCount(0);
                setWishlistCount(0);
                const messaging = getMessaging();
                await deleteToken(messaging);
                setProfileData({
                    full_name: "Guest",
                    email: "",
                    profileImage: ""
                });
                localStorage.clear();
                navigate("/shop-login");

            }
        } catch (error) {
            console.log("Logout Error:", error);
        } finally {
            setIsLoggingOut(false);
        }
    };

    return (
        <header className="site-header mo-left header style-1">
            {/* DESKTOP HEADER - Only visible on desktop */}
            {!isMobile && (
                <>
                    <div className="header-info-bar">
                        <div className="container clearfix">
                            <div className="logo-header logo-dark">
                                <Link to={"/"}><img src={logo} alt="logo" width={180} height={80} /></Link>
                            </div>

                            <div className="extra-nav">
                                <div className="extra-cell">
                                    <ul className="navbar-nav header-right">
                                        <li className="nav-item">
                                            <Link to={"/wishlist"} className="nav-link">
                                                <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 0 24 24" width="22px" fill="#000000">
                                                    <path d="M0 0h24v24H0V0z" fill="none" />
                                                    <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z" />
                                                </svg>
                                                <span className="badge">{wishlistCount}</span>
                                            </Link>
                                        </li>
                                        <Dropdown as="li" className="nav-item">
                                            <Dropdown.Toggle as={Link} to={"/shop-cart"} type="button" className="nav-link box cart-btn i-false">
                                                <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 0 24 24" width="22px" fill="#000000">
                                                    <path d="M0 0h24v24H0V0z" fill="none" />
                                                    <path d="M15.55 13c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.37-.66-.11-1.48-.87-1.48H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2h7.45zM6.16 6h12.15l-2.76 5H8.53L6.16 6zM7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
                                                </svg>
                                                <span className="badge">{cartCount}</span>
                                            </Dropdown.Toggle>
                                        </Dropdown>
                                        <Dropdown
                                            as="li"
                                            className="nav-item dropdown profile-dropdown ms-4 mx-2"
                                            show={showProfileDropdown}
                                            onToggle={(isOpen) => setShowProfileDropdown(isOpen)}
                                        >
                                            <Dropdown.Toggle as="div" className="nav-link i-false" style={{ cursor: 'pointer' }}>
                                                {hasValidProfileImage() ? (
                                                    <img
                                                        src={getFullImageUrl(profileData.profileImage)}
                                                        alt="Profile"
                                                        style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                                                        onError={() => setImageError(true)}
                                                    />
                                                ) : (
                                                    <InitialsAvatar name={profileData.full_name} size={40} />
                                                )}
                                                <div className="profile-info">
                                                    <h6 className="title">{profileData.full_name || 'Guest'}</h6>
                                                    <span>{profileData.email || 'Welcome! Start exploring'}</span>
                                                </div>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="dropdown-menu py-0 dropdown-menu-end">
                                                {isWebsiteLoggedIn() && isProfileCompleted ? (
                                                    <div className="dropdown-body">
                                                        <Link to={"/my-profile"} onClick={() => setShowProfileDropdown(false)} className="dropdown-item d-flex justify-content-between align-items-center ai-icon">
                                                            <div>
                                                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="#000000">
                                                                    <path d="M0 0h24v24H0V0z" fill="none" />
                                                                    <path d="M12 6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2m0 10c2.7 0 5.8 1.29 6 2H6c.23-.72 3.31-2 6-2m0-12C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                                                </svg>
                                                                <span className="ms-2">Profile</span>
                                                            </div>
                                                        </Link>
                                                        <Link to={"/my-requests"} onClick={() => setShowProfileDropdown(false)} className="dropdown-item d-flex justify-content-between align-items-center ai-icon">
                                                            <div>
                                                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="#000000">
                                                                    <path d="M0 0h24v24H0V0z" fill="none" />
                                                                    <path d="M14 2H6c-1.1 0-2 .9-2 2v16l4-4h10c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zm-5 4h8v2H8v-2zm0-4h3v2H8V9z" />
                                                                </svg>
                                                                <span className="ms-2">Custom Requests</span>
                                                            </div>
                                                        </Link>
                                                        <Link to={"/my_following"} onClick={() => setShowProfileDropdown(false)} className="dropdown-item d-flex justify-content-between align-items-center ai-icon">
                                                            <div>
                                                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="#000000">
                                                                    <path d="M0 0h24v24H0V0z" fill="none" />
                                                                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                                                                </svg>
                                                                <span className="ms-2">Following</span>
                                                            </div>
                                                        </Link>
                                                        <Link to={"/my-orders"} onClick={() => setShowProfileDropdown(false)} className="dropdown-item d-flex justify-content-between align-items-center ai-icon">
                                                            <div>
                                                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="#000000">
                                                                    <path d="M0 0h24v24H0V0z" fill="none" />
                                                                    <path d="M15.55 13c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.37-.66-.11-1.48-.87-1.48H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2h7.45zM6.16 6h12.15l-2.76 5H8.53L6.16 6zM7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
                                                                </svg>
                                                                <span className="ms-2">My Order</span>
                                                            </div>
                                                        </Link>
                                                        <Link to="/saved-address" state={{ openForm: true }} onClick={() => setShowProfileDropdown(false)} className="dropdown-item d-flex justify-content-between align-items-center ai-icon">
                                                            <div>
                                                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="#000000">
                                                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5 14.5 7.62 14.5 9 13.38 11.5 12 11.5z" />
                                                                </svg>
                                                                <span className="ms-2">Saved Addresses</span>
                                                            </div>
                                                        </Link>
                                                    </div>
                                                ) : (
                                                    <div className="dropdown-body">
                                                        <Link to={"/shop-login"} className="dropdown-item d-flex justify-content-between align-items-center ai-icon">
                                                            <div><span className="ms-2">Login</span></div>
                                                        </Link>
                                                        <Link to={"/shop-login?mode=register"} className="dropdown-item d-flex justify-content-between align-items-center ai-icon">
                                                            <div><span className="ms-2">Register</span></div>
                                                        </Link>
                                                    </div>
                                                )}
                                                {isWebsiteLoggedIn() && (
                                                    <div className="dropdown-footer">
                                                        <button type="button" onClick={handleLogout} className="btn btn-primary w-100 btnhover btn-sm">Log Out</button>
                                                    </div>
                                                )}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                        <div className="d-none d-lg-block">
                                            <GoogleTranslate />
                                        </div>
                                    </ul>
                                </div>
                            </div>

                            <div className="header-search-nav">
                                <form className="header-item-search" onSubmit={handleSearchSubmit}>
                                    <div className="input-group search-input">
                                        <Dropdown className="dropdown bootstrap-select default-select drop-head">
                                            <Dropdown.Toggle as="div" className="i-false">{selectBtn}
                                                <i className="ms-4 font-10 fa-solid fa-chevron-down"></i>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="category-dropdown-menu">
                                                <Dropdown.Item onClick={() => {
                                                    setSelectBtn('All Categories');
                                                    navigate('/shop');
                                                }}>All Categories</Dropdown.Item>
                                                {searchCategories.map((category) => (
                                                    <Dropdown.Item
                                                        key={category._id}
                                                        onClick={() => {
                                                            setSelectBtn(category.title);
                                                            navigate(`/shop?category=${encodeURIComponent(category.title)}`);
                                                        }}
                                                    >
                                                        {category.title}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                        <input type="text" className="form-control" aria-label="Search artworks input" placeholder="Search meaningful gifts..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                                        <button className="btn" type="submit"><FaSearch /></button>
                                    </div>
                                </form>
                            </div>
                            {/* <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", gap: "10px", padding: "0 0 10px 0" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: "700", fontSize: "14px", color: "#1A1668" }}>
                                    <FaGift size={14} color="#F97316" />
                                    Every purchase supports a student’s dream
                                </div>
                                <div id="google_translate_element"></div>
                            </div> */}
                        </div>
                    </div>
                </>
            )}

            {/* Sticky Header - Common for both but content differs */}
            <div className={`sticky-header main-bar-wraper navbar-expand-lg ${headerFix ? "is-fixed" : ""}`}>
                <div className="main-bar clearfix">
                    <div className="container clearfix">
                        <div className="logo-header logo-dark">
                            <Link to={"/"}><img src={logo} width={180} height={80} alt="logo" /></Link>
                        </div>

                        {/* Mobile Toggle Button - Only visible on mobile */}
                        {isMobile && (
                            <button
                                className={`navbar-toggler collapsed navicon justify-content-end ${sidebarOpen ? 'open' : ''}`}
                                onClick={showSidebar}
                                type="button"
                                aria-label="Toggle navigation menu"
                            >
                                <span></span>
                                <span></span>
                                <span></span>
                            </button>
                        )}

                        {/* DESKTOP NAVIGATION */}
                        {!isMobile && (
                            <>
                                <div className="extra-nav">
                                    <div className="extra-cell">
                                        <a href="https://arttokri.com/student_panel/register" target="_blank" className="btn btn-primary btnhover">Join as Student</a>
                                    </div>
                                </div>
                                <div className={`header-nav navbar-collapse collapse justify-content-start`} id="navbarNavDropdown">
                                    <div className="logo-header logo-dark">
                                        <Link to={"/"}><img src={logo} alt="Logo" width={180} height={80} /></Link>
                                    </div>
                                    <form className="search-input" onSubmit={handleSearchSubmit}>
                                        <div className="input-group">
                                            <input type="text" className="form-control" aria-label="Search artworks input" placeholder="Search meaningful gifts..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                                            <button className="btn" type="submit"><FaSearch /></button>
                                        </div>
                                    </form>
                                    <ul className="nav navbar-nav">
                                        {MenuListArray2.map((data, index) => {
                                            const isRakhi = data.title === 'Explore Rakhis' || data.to === '/handmade-rakhi-online';
                                            const isDiyKits = data.title === 'Explore DIY Kits' || data.to === '/diy-products';
                                            const customClass = isRakhi ? 'rakhi-festival-link' : isDiyKits ? 'diy-kits-menu-link' : '';

                                            return (
                                                <li key={index} className={`${data.content ? 'sub-menu-down' : ''} ${isActiveRoute(data.to) ? 'active' : ''}`}>
                                                    {data.to.startsWith('http') ? (
                                                        <a href={data.to} target="_blank" rel="noreferrer" className={`${isActiveRoute(data.to) ? 'active' : ''} ${customClass}`}>
                                                            <span style={data.title === 'Sell Your Art' ? { fontWeight: '700', fontSize: '15px' } : {}}>
                                                                {data.title}
                                                            </span>
                                                        </a>
                                                    ) : (
                                                        <Link to={data.content ? "#" : data.to} className={`${isActiveRoute(data.to) ? 'active' : ''} ${customClass}`}>
                                                            <span style={data.title === 'Sell Your Art' ? { fontWeight: '700', fontSize: '15px' } : {}}>
                                                                {data.title}
                                                            </span>
                                                        </Link>
                                                    )}
                                                    {data.content && (
                                                        <Collapse in={isActiveRoute(data.to)}>
                                                            <ul className="sub-menu">
                                                                {data.content && data.content.map((data, index) => (
                                                                    <li key={index}><Link to={data.to}>{data.title}</Link></li>
                                                                ))}
                                                                {!isWebsiteLoggedIn() ? (
                                                                    <li><Link to="/shop-login">Login</Link></li>
                                                                ) : (
                                                                    <li><Link to="/my-profile">My Profile</Link></li>
                                                                )}
                                                                <li><Link to="/shop-login?mode=register">Sign Up</Link></li>
                                                            </ul>
                                                        </Collapse>
                                                    )}
                                                </li>
                                            )
                                        })}
                                    </ul>
                                    <div className="dz-social-icon">
                                        <ul>
                                            <li><a className="fab fa-facebook-f" target="_blank" rel="noreferrer" href="https://www.facebook.com/people/Art-Tokri/61582149274629/"></a></li>
                                            <li><a className="fab fa-instagram" target="_blank" rel="noreferrer" href="https://www.instagram.com/art_tokri/"></a></li>
                                            <li><a className="fab fa-youtube" target="_blank" rel="noreferrer" href="https://www.youtube.com/channel/UClUyX-7tway1NF1c2BfIvcQ/"></a></li>
                                            <li><a className="fab fa-twitter" target="_blank" rel="noreferrer" href="https://x.com/Art_Tokri"></a></li>
                                            <li><a className="fab fa-linkedin-in" target="_blank" rel="noreferrer" href="https://www.linkedin.com/company/art-tokri-official/"></a></li>
                                            <li><a className="fab fa-pinterest" target="_blank" rel="noreferrer" href="https://in.pinterest.com/art_tokri/"></a></li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* MOBILE NAVIGATION */}
                        {isMobile && sidebarOpen && (
                            <div className={`header-nav navbar-collapse collapse show`} id="navbarNavDropdown">
                                <div className="logo-header logo-dark">
                                    <Link to={"/"}><img src={logo} alt="Logo" width={180} height={80} /></Link>
                                </div>
                                <form className="search-input" onSubmit={handleSearchSubmit}>
                                    <div className="input-group">
                                        <input type="text" className="form-control" placeholder="Search meaningful gifts..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                                        <button className="btn" type="submit"><FaSearch /></button>
                                    </div>
                                </form>

                                <div className="d-lg-none mobile-language-wrapper">
                                    <GoogleTranslate />
                                </div>

                                <ul className="nav navbar-nav">
                                    {MenuListArray2.map((data, index) => {
                                        const isRakhi = data.title === 'Explore Rakhis' || data.to === '/handmade-rakhi-online';
                                        const isDiyKits = data.title === 'Explore DIY Kits' || data.to === '/diy-products';
                                        const customClass = isRakhi ? 'rakhi-festival-link' : isDiyKits ? 'diy-kits-menu-link' : '';

                                        return (
                                            <li key={index} className={`${data.content ? 'sub-menu-down' : ''} ${isActiveRoute(data.to) ? 'active' : ''}`}>
                                                {data.to.startsWith('http') ? (
                                                    <a href={data.to} target="_blank" rel="noreferrer" onClick={() => setSidebarOpen(false)} className={customClass}>
                                                        <span>{data.title}</span>
                                                    </a>
                                                ) : (
                                                    <Link to={data.to} onClick={() => setSidebarOpen(false)} className={customClass}>
                                                        <span>{data.title}</span>
                                                    </Link>
                                                )}
                                            </li>
                                        );
                                    })}

                                    {/* Mobile Wishlist */}
                                    <li className={isActiveRoute('/wishlist') ? 'active' : ''}>
                                        <Link to="/wishlist" onClick={() => setSidebarOpen(false)}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                Wishlist
                                                {wishlistCount > 0 && (
                                                    <span style={{ backgroundColor: '#dc2626', color: 'white', borderRadius: '20px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>
                                                        {wishlistCount}
                                                    </span>
                                                )}
                                            </span>
                                        </Link>
                                    </li>

                                    {/* Mobile Cart */}
                                    <li className={isActiveRoute('/shop-cart') ? 'active' : ''}>
                                        <Link to="/shop-cart" onClick={() => setSidebarOpen(false)}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                Cart
                                                {cartCount > 0 && (
                                                    <span style={{ backgroundColor: '#2563eb', color: 'white', borderRadius: '20px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>
                                                        {cartCount}
                                                    </span>
                                                )}
                                            </span>
                                        </Link>
                                    </li>

                                    {/* Mobile Auth Links */}
                                    {!isWebsiteLoggedIn() ? (
                                        <>
                                            <li className={isActiveRoute('/shop-login') ? 'active' : ''}>
                                                <Link to="/shop-login" onClick={() => setSidebarOpen(false)}>
                                                    <span>Login</span>
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/shop-login?mode=register" onClick={() => setSidebarOpen(false)}>
                                                    <span>Register</span>
                                                </Link>
                                            </li>
                                        </>
                                    ) : (
                                        <>
                                            <li className={isActiveRoute('/my-profile') ? 'active' : ''}>
                                                <Link to="/my-profile" onClick={() => setSidebarOpen(false)}>
                                                    <span>My Profile</span>
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="#" onClick={(e) => {
                                                    e.preventDefault();
                                                    if (isLoggingOut) return;
                                                    handleLogout();
                                                }}>
                                                    <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
                                                </Link>
                                            </li>
                                        </>
                                    )}
                                </ul>

                                {/* Mobile Join as Student Button */}
                                <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                                    <a href="https://arttokri.com/student_panel/register" target="_blank" rel="noreferrer" onClick={() => setSidebarOpen(false)} style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                        padding: '0.875rem 1rem', backgroundColor: '#F97316', color: 'white', borderRadius: '12px',
                                        textDecoration: 'none', fontWeight: '600', fontSize: '15px', width: '100%'
                                    }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 0 24 24" width="18px" fill="#ffffff">
                                            <path d="M0 0h24v24H0V0z" fill="none" />
                                            <path d="M12 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0-6c-2.2 0-4 1.8-4 4s1.8 4 4 4 4-1.8 4-4-1.8-4-4-4zm0 10c-2.7 0-5.8 1.29-6 2h12c-.2-.71-3.3-2-6-2z" />
                                        </svg>
                                        <span>Join as Student</span>
                                    </a>
                                </div>

                                {/* Mobile Social Icons */}
                                <div className="dz-social-icon">
                                    <ul>
                                        <li><a className="fab fa-facebook-f" target="_blank" rel="noreferrer" href="https://www.facebook.com/people/Art-Tokri/61582149274629/"></a></li>
                                        <li><a className="fab fa-instagram" target="_blank" rel="noreferrer" href="https://www.instagram.com/art_tokri/"></a></li>
                                        <li><a className="fab fa-youtube" target="_blank" rel="noreferrer" href="https://www.youtube.com/channel/UClUyX-7tway1NF1c2BfIvcQ/"></a></li>
                                        <li><a className="fab fa-twitter" target="_blank" rel="noreferrer" href="https://x.com/Art_Tokri"></a></li>
                                        <li><a className="fab fa-linkedin-in" target="_blank" rel="noreferrer" href="https://www.linkedin.com/company/art-tokri-official/"></a></li>
                                        <li><a className="fab fa-pinterest" target="_blank" rel="noreferrer" href="https://in.pinterest.com/art_tokri/"></a></li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    )
}

export default Header;
